import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import ProductNotFound from '../ProductNotFound';
import LoadingOverlay from '../../components/LoadingOverlay';
import { LoadingWrapper } from '../../components/LoadingWrapper';
import CommonUtils from '../../utils/utils-common';
import ForeignLanguageProductDetailView from '../../../shop/language/product/ForeignLanguageProductDetailView';
import CampaignProductDetailView from '../../../shop/campaigns/CampaignProductDetailView';
import ProductDetailView from './ProductDetailView';
import { ShopProduct } from '../../../../models/Product';
import { invalidateProduct, useProduct } from './api/useProduct';
import { useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../../common/UnavailableProductsModal';

const ProductDetailPage: FC = () => {
    const dispatch = useDispatch();
    const { productId } = useParams<{ productId: string }>();
    const { data: product, isLoading } = useProduct(productId, true);

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateProduct(productId).then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    function DetailView() {
        if (CommonUtils.isForeignLanguageClass(product?.productClass)) {
            return (
                <ForeignLanguageProductDetailView
                    product={product as ShopProduct}
                    dispatch={dispatch}
                    onAddPrice={changePrice}
                />
            );
        } else if (CommonUtils.isCampaignClass(product?.productClass)) {
            return (
                <CampaignProductDetailView
                    product={product as ShopProduct}
                    dispatch={dispatch}
                    onAddPrice={changePrice}
                />
            );
        } else {
            return <ProductDetailView dispatch={dispatch} product={product as ShopProduct} onAddPrice={changePrice} />;
        }
    }

    if (isEmpty(product)) {
        return <ProductNotFound productId={productId} />;
    }

    return (
        <div className={classnames({ 'min-h-screen': isLoading })}>
            {isLoading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={isLoading} />
                </LoadingWrapper>
            ) : (
                <DetailView />
            )}
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </div>
    );
};

export default ProductDetailPage;
